<template>
  <div class="py-2 my-2 help-container">
    <h1 class="separator py-4">OCCUPANCY WORKSHEET > RENTAL DEDUCTIONS TAB</h1>
    <span class="banner py-3 mb-4">
      <div>This tab will allow you to budget for your concessions and your bad debt. Concessions are broken down into Move Ins, Renewals and Other Concessions.</div>
      <div>This tab is highly affected by the choices you made on the Settings > Lease Calculations tab. This will be discussed further in the sections below.</div>
    </span>

    <!--Help Sections-->
    <v-expansion-panels v-model="openSectionInternal">
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(0)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(0)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Top Row Options</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>If the <strong>Clear & Reset</strong> button is clicked, a popup box will appear that reads "<em>This will clear all data from this tab and add back any starting default numbers. Are you sure you want to do this? Cancel or Yes</em>"</div>
          <div>If you select Yes, any number that has been changed on this tab, will be reset to the initial starting numbers</div>
          <v-divider></v-divider>
          <div><strong>Forecasted # Move-Ins</strong>: Total Move Ins is from the Move Ins/Move Outs tab (top summary section). Greyed out, not editable.</div>
          <div><strong>Forecasted # Renewals</strong>: Projected # Renewed is from the Move Ins/Move Outs tab. Greyed out, not editable</div>
          <ul>
            <li>If Property Level was selected for the Move Ins/Move Outs tab - this will be the same as the Projected # Renewed line</li>
            <li>If Unit Type Level was selected for the Move Ins/Move Outs tab - this will be the sum of each of the Projected # Renewed lines from each Unit Type.</li>
          </ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(1)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(1)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Calculations - Move In</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div><strong>Recurring Move In Concession</strong></div>
          <ul>
            <li>This line will appear if the user has selected "There will also be a concession of ___% of Market Rent" in the Lease Calculation section of the settings page</li>
            <li>Initial numbers:</li>
            <li class="no-bullet">
              <ul>
                <li>This line will initially load all of the Recurring Charge amounts for the Transaction Category selected in the Move In section of the setting page</li>
                <li>It will take the amount as of the end of the prior budget year and use that number for the entire budget year.</li>
                <li>This is Editable</li>
              </ul>
            </li>
          </ul>
          <div><strong>New Move In Concession</strong></div>
          <ul><li>This line will always appear. It is initially blank, editable.</li></ul>
          <div><strong>Total Move In Concession</strong></div>
          <ul><li>This will display the total of Recurring Move In Concession (if it appears) plus New Move In Concession. Not editable, greyed out.</li></ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(2)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(2)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Calculations - Renewals</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div><strong>Recurring Renewal Concession</strong></div>
          <ul>
            <li>This line will appear if the user has selected "Renewals will be at Market Rent AND will also offer a recurring concession" in the Lease Calculations section of the settings page</li>
            <li>Initial numbers:</li>
            <li class="no-bullet">
              <ul>
                <li>This line will initially load all of the Recurring Charge amounts for the Transaction Category selected in the Renewal section of the setting page</li>
                <li>It will take the amount as of the end of the prior budget year and use that number for the entire budget year.</li>
                <li>This is editable</li>
              </ul>
            </li>
          </ul>
          <div><strong>New Renewal Concession</strong></div>
          <ul><li>This line will always appear. It is initially blank, editable.</li></ul>
          <div><strong>Total Renewal Concession</strong></div>
          <ul><li>This will display the total of Recurring Renewal Concession (if it appears) plus New Move In Concession. Not editable, greyed out.</li></ul>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(3)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(3)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Calculations - Other Concessions</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>For each transaction category selected in Settings > Additional Concessions and Bad Debt tab, there will be a line item.</div>
          <div>The title of the line will be the Transaction Category name. The amount will be initially blank, but is editable</div>
          <div><strong>Total Add'l Concessions</strong> will sum all rows in this section and is not editable.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(4)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(4)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Calculations - Total Concessions</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Will add Total Move-In Concession + Total Renewal Concession + Total Add'l Concessions</div>
          <div>These TOTAL CONCESSIONS numbers will appear in the top Occupancy summary section on the Rental Deduction line</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(5)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(5)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Calculations - Bad Debt</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div><strong>Estimated Skips/Evictions</strong> will display the information from the Move Ins / Move Outs tab, Estimated Skips/Evictions. This is to assist you in your decisions.</div>
          <div><strong>Bad Debt Line</strong></div>
          <ul>
            <li>The title of the line will be the Transaction Category name selected in the Other Concessions and Bad Debt section of the Setting page</li>
            <li>The amount will be based on the formula selected in Settings > Other Concessions and Bad Debt. These are the three possible choices:</li>
            <li class="no-bullet">
              <ul>
                <li>1. Bad Debt will be a percentage of budgeted GL Account</li>
                <li class="no-bullet">
                  <ul>
                    <li>
                      The Selected GL account multiplied times their percentage is what populates in each month of the budget for the bad debt line. Editable
                    </li>
                  </ul>
                </li>
                <li>2. Bad Debt will be Average Market Rent multiplied by the number of Skips/Evictions</li>
                <li class="no-bullet">
                  <ul>
                    <li>
                      In this choice, Average Market Rent is multiplied by each month's number of Skips/Evictions (shown on the line above). You can verify Average Market Rent by looking at the top summary section of the Occupancy tab. Editable
                    </li>
                  </ul>
                </li>
                <li>3. Manual Entry</li>
                <li class="no-bullet">
                  <ul>
                    <li>
                      In this choice, verify that nothing populates. Editable
                    </li>
                  </ul>
                </li>
              </ul>
            </li>
          </ul>
          <div><strong>Total Bad Debt</strong> will sum the row above and is not editable</div>
          <div>This will appear in the top Occupancy summary section on the Bad Debt line.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel>
        <v-expansion-panel-header :color="getSectionColorFor(6)">
          <v-icon small class="icon" slot="actions" :color="getIconColorFor(6)">$vuetify.icons.expand</v-icon>
          <span class="header pl-3"><strong>Right-Side Icons & Ellipsis Menu Options</strong></span>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="help-content">
          <div>Within each of the Occupancy tabs you will have additional options on the right-side of the screen.</div>
          <img src="../../assets/help/a.png" />
          <div>
            <img class="small" src="../../assets/help/b.png" />
            <span>This icon will bring up a help screen specific to this page</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/c.png" />
            <span>This icon will open the Comments and Flags screen which will allow you to add a general comment, create a flagged comment, or reply to an existing comment. If it is empty in the middle, there are no comments or flags.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/d.png" />
            <span>If it has changed to a solid blue, then comments have been entered.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/e.png" />
            <span>If it has changed to an orange flag, then an unresolved flag or issue exists. Someone will need to click into the Comments and Flags screen and select Resolve the outstanding issue. Once resolved, the icon will change back to blue.</span>
          </div>
          <div>
            <img class="small" src="../../assets/help/f.png" />
            <span>Clicking this ellipsis will bring up a new menu of options.</span>
          </div>
          <br />
          <div><strong>Add Flag or Comment</strong> will bring up the same screen as the center icon allowing you to add a comment, a flagged comment or resolve a comment</div>
          <div><strong>Clear & Reset</strong> will clear out any data that has been added and reset this tab back to the default values when the page was first loaded. This only applies to the specific tab you are on. It will also popup a message to explain what is about to happen and to allow you to confirm or cancel.</div>
        </v-expansion-panel-content>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Prefer to watch a video? <a @click="openExternalTopic('BudgetsPro Occupancy Rental Deducations Tab')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
      <v-expansion-panel readonly>
        <v-expansion-panel-header>
          <v-icon slot="actions"></v-icon>
          <span class="black--text pointer">Want to see all BudgetsPro help articles? <a @click="openExternalTopic('BudgetsPro')">Click Here</a></span>
        </v-expansion-panel-header>
      </v-expansion-panel>
    </v-expansion-panels>
  </div>
</template>

<script>
  import HelpMixin from '@/mixins/Help.js'
  export default {
    name: 'OccupancyRentalDeductionsHelp',
    mixins: [HelpMixin]
  }
</script>

<style lang="scss" scoped>
  .no-bullet {
    list-style-type: none;
  }
  .icon {
      order: 0;
  }
  .header {
      order: 1;
  }
  img {
    max-width: 650px;
  }
  img.small {
    width: 35px;
  }
  .help-container {
      border-bottom: 1px solid var(--v-resGrey-lighten1)
  }
  .help-content div,
  .help-content li {
      margin-top:10px !important;
      margin-bottom:10px !important;
  }
  .banner {
    text-align: center;
    background-color: var(--v-resGrey-lighten1);
    display: block;
  }
  .pointer {
      cursor: pointer !important;
  }
  .separator {
    display: flex;
    align-items: center;
    text-align: center;
  }
  .separator::before,
  .separator::after {
    content: '';
    flex: 1;
    border-bottom: 1px solid var(--v-resGrey-darken1);
  }

  .separator:not(:empty)::before {
    margin-right: .25em;
  }

.separator:not(:empty)::after {
  margin-left: .25em;
}
</style>
